/**
 * Las partidas de gasto, en un solo sitio.
 *
 * Habia tres motores que sumaban el gasto por separado y cada uno con su lista:
 * añadir la cuota patronal obligo a tocar los tres. Si el proximo campo se
 * añade a dos de ellos, las cifras divergen y nada avisa —ya paso con el
 * renting, que se perdia al guardar el cierre (issue #18, punto 1)—.
 *
 * Aqui esta la lista canonica y como se llama cada partida en cada sitio. Una
 * prueba comprueba que ningun mapa se deje una fuera.
 *
 * Ver `docs/AUDITORIA_2026-08-09.md` (A1) y `specs/010-partidas-de-gasto/`.
 */

/** Gastos que no dependen del volumen de reparto. */
export const PARTIDAS_FIJAS = [
    'salarios',
    'ssTrabajador',
    'ssEmpresa',
    'cuotaAutonomo',
    'seguros',
    'gestoria',
    'prl',
    'contabilidad',
    'serviciosProfesionales',
    'flyder',
    'marketing',
    'serviciosRepaart',
] as const;

/** Gastos que suben y bajan con la operacion. */
export const PARTIDAS_VARIABLES = [
    'combustible',
    'reparaciones',
    'incidencias',
    'otros',
] as const;

export type PartidaFija = typeof PARTIDAS_FIJAS[number];
export type PartidaVariable = typeof PARTIDAS_VARIABLES[number];
export type Partida = PartidaFija | PartidaVariable;

export const TODAS_LAS_PARTIDAS: readonly Partida[] = [...PARTIDAS_FIJAS, ...PARTIDAS_VARIABLES];

/**
 * Como se llama cada partida en el documento guardado (`MonthlyData`).
 *
 * Son los nombres que hay en Firestore. **No se cambian**: hay cierres
 * historicos escritos con ellos (constitucion, regla 4).
 */
export const CAMPO_GUARDADO: Record<Partida, string> = {
    salarios: 'salaries',
    ssTrabajador: 'socialSecurity',
    ssEmpresa: 'employerSocialSecurity',
    cuotaAutonomo: 'quota',
    seguros: 'insurance',
    gestoria: 'agencyFee',
    prl: 'prlFee',
    contabilidad: 'accountingFee',
    serviciosProfesionales: 'services',
    flyder: 'appFlyder',
    marketing: 'marketing',
    serviciosRepaart: 'repaartServices',
    combustible: 'gasoline',
    reparaciones: 'repairs',
    incidencias: 'incidents',
    otros: 'otherExpenses',
};

/**
 * Como se llama cada partida en el formulario del cierre (`ExpenseData`).
 *
 * Difiere del guardado en cuatro nombres, por razones historicas.
 */
export const CAMPO_FORMULARIO: Record<Partida, string> = {
    salarios: 'payroll',
    ssTrabajador: 'socialSecurity',
    ssEmpresa: 'employerSocialSecurity',
    cuotaAutonomo: 'quota',
    seguros: 'insurance',
    gestoria: 'agencyFee',
    prl: 'prlFee',
    contabilidad: 'accountingFee',
    serviciosProfesionales: 'professionalServices',
    flyder: 'appFlyder',
    marketing: 'marketing',
    serviciosRepaart: 'repaartServices',
    combustible: 'fuel',
    reparaciones: 'repairs',
    incidencias: 'incidents',
    otros: 'other',
};

/** Nombres de campo guardados, en el orden canonico. */
export const CAMPOS_FIJOS_GUARDADOS = PARTIDAS_FIJAS.map((p) => CAMPO_GUARDADO[p]);
export const CAMPOS_VARIABLES_GUARDADOS = PARTIDAS_VARIABLES.map((p) => CAMPO_GUARDADO[p]);

const numero = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * Suma las partidas de un objeto, sea cual sea su juego de nombres.
 *
 * Recorre la lista canonica, asi que una partida nueva entra en todos los
 * consumidores a la vez o no entra en ninguno.
 */
export const sumarPartidas = (
    datos: Record<string, unknown>,
    mapa: Record<Partida, string>,
    partidas: readonly Partida[] = TODAS_LAS_PARTIDAS
): number => partidas.reduce((suma, p) => suma + numero(datos[mapa[p]]), 0);
